import {
  GetMyStudyProgrammes,
  GetMyStudyProgrammes_studyProgrammeList,
  GetStudyProgrammeContainerContexts,
  StudyProgrammeWithContainerContextsFragment,
} from "__generated__/types";
import { useSimpleQuery } from "common/graphql/useSimpleQuery";
import { studyProgrammeWithContainerContextsLightFragment } from "domain/common/study-programme-container-fragments";
import gql from "graphql-tag";
import {
  createContext,
  Dispatch,
  default as React,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from "react";
import { ActionMap } from "./ContextTypes";

export const GET_MY_STUDYPROGRAMMES = gql`
  query GetMyStudyProgrammes {
    studyProgrammeList(filter: { onlyMine: true }) {
      title
      id
      status
    }
  }
`;

const GET_CONTAINER_CONTEXTS_LIGHT = gql`
  query GetStudyProgrammeContainerContextsLight {
    getStudyProgrammeContainerContextsLight {
      ...StudyProgrammeWithContainerContextsLightFragment
    }
  }
  ${studyProgrammeWithContainerContextsLightFragment}
`;

export enum StudyProgrammesActionTypes {
  SetData = "SetData",
  SetContainerStructure = "SetContainerStructure",
}

type StudyProgrammesActionPayload = {
  [StudyProgrammesActionTypes.SetData]: {
    myStudyProgrammes: GetMyStudyProgrammes_studyProgrammeList[];
  };
  [StudyProgrammesActionTypes.SetContainerStructure]: {
    containerStructure: StudyProgrammeWithContainerContextsFragment;
  };
};

export type StudyProgrammesActions =
  ActionMap<StudyProgrammesActionPayload>[keyof ActionMap<StudyProgrammesActionPayload>];

interface StudyProgrammes {
  studyProgrammes: GetMyStudyProgrammes_studyProgrammeList[];
  containerStructure: StudyProgrammeWithContainerContextsFragment;
}

const initialState: StudyProgrammes = {
  studyProgrammes: [],
  containerStructure: {
    __typename: "GetExamSyllabusContainerContextsPayloadLight",
    studyProgrammesWithContainers: [],
  },
};

const StudyProgrammesContext = createContext<{
  state: StudyProgrammes;
  dispatch: Dispatch<StudyProgrammesActions>;
  refetch: () => void;
}>({
  state: initialState,
  dispatch: () => null,
  refetch: () => {
    console.log("refetching");
  },
});

const myStudyProgrammesReducer = (
  state: StudyProgrammes,
  action: StudyProgrammesActions,
): StudyProgrammes => {
  switch (action.type) {
    case StudyProgrammesActionTypes.SetData:
      return {
        ...state,
        studyProgrammes: action.payload.myStudyProgrammes,
      };
    case StudyProgrammesActionTypes.SetContainerStructure:
      return {
        ...state,
        containerStructure: action.payload.containerStructure,
      };
    default:
      return state;
  }
};

export const MyStudyProgrammesProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(myStudyProgrammesReducer, initialState);
  const { data: studyProgrammesData } = useSimpleQuery<GetMyStudyProgrammes>(
    GET_MY_STUDYPROGRAMMES,
  );
  const { data: studyProgrammeContainersData, refetch } =
    useSimpleQuery<GetStudyProgrammeContainerContexts>(
      GET_CONTAINER_CONTEXTS_LIGHT,
    );

  useEffect(() => {
    if (
      studyProgrammesData &&
      state.studyProgrammes !== studyProgrammesData.studyProgrammeList
    ) {
      dispatch({
        type: StudyProgrammesActionTypes.SetData,
        payload: {
          myStudyProgrammes: studyProgrammesData.studyProgrammeList,
        },
      });
    }
  }, [studyProgrammesData, state.studyProgrammes]);

  useEffect(() => {
    if (
      studyProgrammeContainersData &&
      state.containerStructure !==
        studyProgrammeContainersData.getStudyProgrammeContainerContexts
    ) {
      dispatch({
        type: StudyProgrammesActionTypes.SetContainerStructure,
        payload: {
          containerStructure:
            studyProgrammeContainersData.getStudyProgrammeContainerContexts,
        },
      });
    }
  }, [studyProgrammeContainersData, state.containerStructure]);

  const contextValue = useMemo(
    () => ({ state, dispatch, refetch }),
    [state, refetch],
  );

  return (
    <StudyProgrammesContext.Provider value={contextValue}>
      {children}
    </StudyProgrammesContext.Provider>
  );
};

export const useMyStudyProgrammes = (): {
  state: StudyProgrammes;
  dispatch: React.Dispatch<StudyProgrammesActions>;
  refetch: () => void;
} => {
  const context = useContext(StudyProgrammesContext);

  if (context === undefined) {
    throw new Error(
      "`useMyStudyProgrammes` must be used within a `MyStudyProgrammesProvider`.",
    );
  }

  return context;
};
